// src/components/WhatCanIWin.js
import React, { useState, useEffect } from 'react';
import { collection, getDoc, getDocs, query, where, doc } from 'firebase/firestore';
import { firestore } from '../firebase';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPublicDefaultEventId } from '../utils/activeEvent';
import { fetchSponsorsMergedForEvent } from '../utils/sponsorQueries';
import { isPublicWhatCanIWinPrizeStatus } from '../utils/prizeStatus';
import './WhatCanIWin.css';

const GIVEAWAY_LABELS = {
  plinko: 'Plinko',
  bucket_raffle: 'Bucket Raffle',
  awards: 'Awards',
  general_raffle: 'General Raffle',
  goody_bag: 'Goody Bag',
  prize: 'Prize',
  all_freshlings: 'All Freshlings',
  unknown: 'Unknown',
};

const PUBLIC_VISIBLE_STATUSES = ['confirmed', 'received', 'printed', 'awarded'];

const WhatCanIWin = () => {
  const [searchParams] = useSearchParams();
  const eventIdParam = searchParams.get('event');
  const [activeEvent, setActiveEvent] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      try {
        let event = null;
        if (eventIdParam) {
          const eventSnap = await getDoc(doc(firestore, 'events', eventIdParam));
          if (!eventSnap.exists() || cancelled) return;
          event = { id: eventSnap.id, ...eventSnap.data() };
        } else {
          const id = await getPublicDefaultEventId();
          if (!id || cancelled) return;
          const eventSnap = await getDoc(doc(firestore, 'events', id));
          if (!eventSnap.exists() || cancelled) return;
          event = { id: eventSnap.id, ...eventSnap.data() };
        }
        setActiveEvent(event);

        const [mergedSponsors, prizesSnap] = await Promise.all([
          fetchSponsorsMergedForEvent(event.id),
          getDocs(query(
            collection(firestore, 'prizes'),
            where('eventId', '==', event.id),
            where('status', 'in', PUBLIC_VISIBLE_STATUSES)
          )),
        ]);
        if (cancelled) return;
        setSponsors(mergedSponsors.filter((s) => !s.hidden && (s.type || 'none') !== 'none'));
        setPrizes(
          prizesSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((p) => !p.unlisted && isPublicWhatCanIWinPrizeStatus(p.status))
        );
      } catch (err) {
        if (!cancelled) console.error('WhatCanIWin fetch error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [eventIdParam]);

  const getSponsor = (sponsorId) => sponsors.find((s) => s.id === sponsorId);

  const byGiveaway = prizes.reduce((acc, p) => {
    const type = p.giveawayType || 'general_raffle';
    if (!acc[type]) acc[type] = [];
    acc[type].push(p);
    return acc;
  }, {});

  const order = ['plinko', 'bucket_raffle', 'awards', 'general_raffle', 'goody_bag', 'prize', 'all_freshlings'];
  const sections = order.filter((k) => byGiveaway[k]?.length);

  if (loading) {
    return (
      <div className="what-can-i-win">
        <p className="what-can-i-win-loading">Loading…</p>
      </div>
    );
  }

  if (!activeEvent) {
    return (
      <div className="what-can-i-win">
        <p className="what-can-i-win-empty">No active event.</p>
        <button type="button" className="what-can-i-win-btn" onClick={() => navigate('/')}>Back to home</button>
      </div>
    );
  }

  return (
    <div className="what-can-i-win">
      <header className="what-can-i-win-header">
        <div className="what-can-i-win-header-inner">
          <img
            src="/fresh-meet-logo.png"
            alt="Fresh Meet"
            className="what-can-i-win-logo"
            onClick={() => navigate('/')}
          />
          <h1 className="what-can-i-win-title">What can I win?</h1>
          <p className="what-can-i-win-subtitle">{activeEvent.title}</p>
          <button type="button" className="what-can-i-win-btn what-can-i-win-btn-back" onClick={() => navigate(-1)}>
            Back
          </button>
        </div>
      </header>

      <main className="what-can-i-win-main">
        {sections.length === 0 && (
          <p className="what-can-i-win-empty">No prizes listed yet. Check back later!</p>
        )}
        {sections.map((giveawayKey) => (
          <section key={giveawayKey} className="what-can-i-win-section">
            <h2 className="what-can-i-win-section-title">
              {GIVEAWAY_LABELS[giveawayKey] || giveawayKey}
            </h2>
            <div className="what-can-i-win-cards">
              {byGiveaway[giveawayKey].map((prize) => {
                const sponsor = getSponsor(prize.sponsorId);
                const nameOrDesc = prize.name || prize.description || 'Prize';
                const desc = prize.description && prize.name ? prize.description : (prize.description || prize.name || '');
                const exp = prize.expirationDate;
                const expStr = exp && (exp.toDate ? exp.toDate().toISOString().slice(0, 10) : (typeof exp === 'string' ? exp.slice(0, 10) : ''));
                const expDisplay = expStr ? new Date(expStr + 'T12:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : null;
                const imageUrl = (prize.imageUrl || '').trim();
                const winnerPhotoUrls = (Array.isArray(prize.winnerPhotoUrls) ? prize.winnerPhotoUrls : []).filter((u) => (u || '').trim());
                if (!winnerPhotoUrls.length && (prize.winnerPhotoUrl || '').trim()) winnerPhotoUrls.push(prize.winnerPhotoUrl.trim());
                return (
                  <div key={prize.id} className={`what-can-i-win-card${!sponsor ? ' what-can-i-win-card--no-sponsor' : ''}`}>
                    {sponsor && (
                      <a
                        href={(sponsor.website || '').trim() || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="what-can-i-win-card-sponsor"
                        aria-label={`Sponsor: ${sponsor.name}`}
                      >
                        {sponsor.logo && !sponsor.logo.includes('drive.google.com') ? (
                          <img src={sponsor.logo} alt="" className="what-can-i-win-card-logo" />
                        ) : (
                          <span className="what-can-i-win-card-sponsor-name">{sponsor.name}</span>
                        )}
                      </a>
                    )}
                    <div className="what-can-i-win-card-body">
                      {imageUrl && (
                        <div className="what-can-i-win-card-image-wrap">
                          <img src={imageUrl} alt="" className="what-can-i-win-card-image" />
                        </div>
                      )}
                      {winnerPhotoUrls.length > 0 && (
                        <div className="what-can-i-win-card-winner-block">
                          <span className="what-can-i-win-card-winner-label">{winnerPhotoUrls.length === 1 ? 'Winner' : 'Winners'}</span>
                          <div className={`what-can-i-win-card-winner-photos${winnerPhotoUrls.length > 1 ? ' what-can-i-win-card-winner-photos--multi' : ''}`}>
                            {winnerPhotoUrls.map((url, i) => (
                              <div key={i} className="what-can-i-win-card-image-wrap what-can-i-win-card-winner-photo">
                                <img src={url} alt="" className="what-can-i-win-card-image" />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <h3 className="what-can-i-win-card-title">
                        {prize.quantity > 1 ? `${prize.quantity}× ` : ''}{nameOrDesc}
                      </h3>
                      {desc && desc !== nameOrDesc && (
                        <p className="what-can-i-win-card-desc">{desc}</p>
                      )}
                      {expDisplay && (
                        <p className="what-can-i-win-card-expires">Valid until {expDisplay}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
};

export default WhatCanIWin;
